/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  UserGroupIcon,
  EnvelopeIcon,
  UserPlusIcon,
  ClockIcon,
  ShieldCheckIcon,
  EyeIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline';
import { organizationService } from '../../services/api';
import { Organization, OrganizationMember, OrgInvitation, OrgRole } from '../../types';
import InviteOrgMemberModal from '../../components/organization/InviteOrgMemberModal';
import ConfirmModal from '../../components/common/ConfirmModal';

const ASSIGNABLE_ROLES: OrgRole[] = ['org_admin', 'org_member', 'org_billing_admin'];

interface ConfirmAction {
  type: 'remove' | 'transfer';
  userId: string;
  label: string;
}

const roleBadge = (role: OrgRole) => {
  switch (role) {
    case 'org_owner':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs font-medium rounded-full">
          Owner
        </span>
      );
    case 'org_admin':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs font-medium rounded-full">
          <ShieldCheckIcon className="w-3.5 h-3.5" />
          Admin
        </span>
      );
    case 'org_billing_admin':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-500/20 text-green-400 text-xs font-medium rounded-full">
          <CurrencyDollarIcon className="w-3.5 h-3.5" />
          Billing admin
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs font-medium rounded-full">
          <EyeIcon className="w-3.5 h-3.5" />
          Member
        </span>
      );
  }
};

export default function OrgMembersTab({ org, isAdmin, isOwner }: { org: Organization; isAdmin: boolean; isOwner: boolean }) {
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [invitations, setInvitations] = useState<OrgInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);
  const [processing, setProcessing] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await organizationService.getMembers(org.id);
      setMembers(res.data.members || []);
      if (isAdmin) {
        const inv = await organizationService.getInvitations(org.id);
        setInvitations((inv.data.invitations || []).filter((i: OrgInvitation) => i.status === 'pending'));
      }
    } catch {
      toast.error('Failed to load members');
    } finally {
      setLoading(false);
    }
  }, [org.id, isAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  const changeRole = async (userId: string, role: string) => {
    try {
      await organizationService.updateMemberRole(org.id, userId, role);
      toast.success('Role updated');
      load();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to update role');
    }
  };

  const handleConfirm = async () => {
    if (!confirmAction) return;
    setProcessing(true);
    try {
      if (confirmAction.type === 'remove') {
        await organizationService.removeMember(org.id, confirmAction.userId);
        toast.success('Member removed');
      } else {
        await organizationService.transferOwner(org.id, confirmAction.userId);
        toast.success('Ownership transferred');
      }
      await load();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Action failed');
    } finally {
      setProcessing(false);
      setConfirmAction(null);
    }
  };

  const confirmModalProps = confirmAction
    ? confirmAction.type === 'remove'
      ? {
          title: 'Remove member',
          message: `Remove ${confirmAction.label} from the organisation? They will lose access to org-scoped resources.`,
          confirmText: 'Remove member',
          variant: 'danger' as const,
        }
      : {
          title: 'Transfer ownership',
          message: `Transfer ownership to ${confirmAction.label}? You will become an org admin.`,
          confirmText: 'Transfer ownership',
          variant: 'warning' as const,
        }
    : null;

  return (
    <div className="card space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-white">Members</h3>
        {isAdmin && (
          <button onClick={() => setShowInvite(true)} className="btn-primary flex items-center gap-2">
            <UserPlusIcon className="w-5 h-5" />
            Invite member
          </button>
        )}
      </div>

      {loading ? (
        <div className="animate-pulse space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 bg-gray-800 rounded-lg" />
          ))}
        </div>
      ) : members.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <UserGroupIcon className="w-12 h-12 mx-auto text-gray-600 mb-3" />
          <p className="font-medium">No members yet</p>
        </div>
      ) : (
        <div className="divide-y divide-gray-700">
          {members.map((m) => {
            const isTheOwner = m.role === 'org_owner';
            const label = m.user?.name || m.user?.email || 'this member';
            return (
              <div key={m.id} className="py-4 flex items-center justify-between gap-4">
                <div className="flex items-center gap-4 min-w-0">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white font-semibold shrink-0">
                    {(m.user?.email || 'U')[0].toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-white truncate">{m.user?.name || m.user?.email}</p>
                    <p className="text-sm text-gray-400 truncate">{m.user?.email}</p>
                  </div>
                </div>

                <div className="flex items-center gap-4 shrink-0">
                  {isAdmin && !isTheOwner ? (
                    <select
                      value={m.role}
                      onChange={(e) => changeRole(m.user_id, e.target.value)}
                      className="px-2 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      {ASSIGNABLE_ROLES.map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </select>
                  ) : (
                    roleBadge(m.role)
                  )}

                  {isOwner && !isTheOwner && (
                    <button
                      onClick={() => setConfirmAction({ type: 'transfer', userId: m.user_id, label })}
                      className="text-xs text-gray-400 hover:text-gray-200"
                    >
                      Make owner
                    </button>
                  )}
                  {isAdmin && !isTheOwner && (
                    <button
                      onClick={() => setConfirmAction({ type: 'remove', userId: m.user_id, label })}
                      className="text-xs text-red-400 hover:text-red-300"
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {isAdmin && invitations.length > 0 && (
        <div className="pt-2 border-t border-gray-700">
          <h4 className="text-sm font-medium text-gray-300 mb-2">Pending invitations</h4>
          <ul className="text-sm space-y-1">
            {invitations.map((i) => (
              <li key={i.id} className="flex items-center justify-between py-1.5 border-b border-gray-800 last:border-0">
                <span className="flex items-center gap-2 text-gray-300">
                  <EnvelopeIcon className="w-4 h-4 text-gray-500" />
                  {i.email}
                </span>
                <span className="flex items-center gap-1 text-xs text-gray-500">
                  {i.org_role || '-'} <ClockIcon className="w-3.5 h-3.5 ml-1" /> expires {new Date(i.expires_at).toLocaleDateString()}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {showInvite && <InviteOrgMemberModal orgId={org.id} onClose={() => setShowInvite(false)} onInvited={load} />}

      {confirmAction && confirmModalProps && (
        <ConfirmModal
          isOpen={!!confirmAction}
          onClose={() => setConfirmAction(null)}
          onConfirm={handleConfirm}
          isLoading={processing}
          {...confirmModalProps}
        />
      )}
    </div>
  );
}
